/* VYAGO 3D can — <vyago-can> custom element.

   Shows the poster still immediately (that image is the LCP candidate), then
   lazily imports the three.js bundle once the element is within 300px of the
   viewport and swaps in the live canvas. If WebGL is unavailable or the import
   fails the poster simply stays — that is the intended fallback, not an error.

   Attributes
     flavour    tropical | lemonlime | kiwiguava
     lighting   hero | studio | rim          (default studio)
     urls       JSON map of versioned texture URLs (Shopify asset_url)
     src        URL of vyago-can.bundle.js   (required)
     sway       present = gentle oscillation around the front face
     spin       "false" disables idle rotation
     zoom       camera pull-back multiplier, default 1
     no-zoom    present = disable click-to-zoom
     static     present = never load 3D, poster only

   Properties / methods
     .flavour = 'lemonlime'   swaps label and lighting tint
     .showPanel('nutrition')  rotates a label panel to face the viewer
     .ready                   Promise resolving when the canvas is live
*/
(() => {
  if (customElements.get('vyago-can')) return;

  const PANELS = ['front', 'benefits', 'story', 'nutrition'];
  const NAMES = { tropical: 'Tropical Punch', lemonlime: 'Lemon Lime', kiwiguava: 'Kiwi Guava' };

  let modulePromise = null;
  function loadModule(src) {
    if (!modulePromise) {
      // Resolve against the document first. A dynamic import() inside a
      // classic script treats "assets/x.js" as a bare specifier and throws
      // before it ever hits the network; Shopify's asset_url happens to be
      // absolute, but this keeps it working on any path.
      const url = new URL(src, document.baseURI).href;
      modulePromise = import(/* webpackIgnore: true */ url).catch((e) => {
        modulePromise = null;
        throw e;
      });
    }
    return modulePromise;
  }

  const hasWebGL = (() => {
    let cached;
    return () => {
      if (cached !== undefined) return cached;
      try {
        const c = document.createElement('canvas');
        cached = !!(c.getContext('webgl2') || c.getContext('webgl'));
      } catch (e) {
        cached = false;
      }
      return cached;
    };
  })();

  class VyagoCan extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      this._poster = this.querySelector('[data-vyago-poster]');
      this._mount = document.createElement('div');
      this._mount.className = 'vyago-can__stage';
      this.insertBefore(this._mount, this.firstChild);

      if (this.hasAttribute('static') || !hasWebGL()) return;

      const src = this.getAttribute('src');
      if (!src) return;

      let urls = null;
      try { urls = JSON.parse(this.getAttribute('urls') || 'null'); } catch (e) { urls = null; }
      if (!urls) return;

      this._io = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this._io.disconnect();
          this._boot(src, urls);
        },
        { rootMargin: '300px' }
      );
      this._io.observe(this);
    }

    async _boot(src, urls) {
      let mod;
      try {
        mod = await loadModule(src);
      } catch (e) {
        // Poster stays, which is the intended fallback, but say so once so a
        // misconfigured asset path is findable rather than silent.
        if (window.console) console.warn('[vyago-can] 3D bundle failed to load:', e);
        this.setAttribute('data-fallback', '');
        return;
      }
      if (!this.isConnected) return;

      let resolveReady;
      this.ready = new Promise((r) => { resolveReady = r; });

      this._api = mod.createVyagoCan(this._mount, {
        urls: urls,
        flavour: this.getAttribute('flavour') || 'tropical',
        lighting: this.getAttribute('lighting') || 'studio',
        idleSpin: this.getAttribute('spin') !== 'false',
        idleSway: this.hasAttribute('sway'),
        pointerTilt: this.getAttribute('tilt') !== 'false',
        drag: this.getAttribute('drag') !== 'false',
        zoomOnClick: !this.hasAttribute('no-zoom'),
        zoom: parseFloat(this.getAttribute('zoom')) || 1,
        onReady: () => {
          if (!this.isConnected) return;
          this.setAttribute('data-live', '');
          resolveReady(this._api);
          this.dispatchEvent(new CustomEvent('vyago:ready', { bubbles: true }));
        },
      });
    }

    get flavour() {
      return this.getAttribute('flavour') || 'tropical';
    }

    set flavour(key) {
      if (!NAMES[key] || key === this.flavour) return;
      this.setAttribute('flavour', key);
      if (this._poster) {
        const next = this._poster.getAttribute('data-poster-' + key);
        if (next) this._poster.src = next;
        this._poster.alt = 'VYAGO ' + NAMES[key] + ' can';
      }
      if (this._api) this._api.setFlavour(key);
    }

    showPanel(name) {
      if (this._api && PANELS.indexOf(name) !== -1) this._api.showPanel(name);
    }

    setSpin(on) {
      if (this._api) this._api.setSpin(on);
    }

    disconnectedCallback() {
      this._io && this._io.disconnect();
      this._api && this._api.dispose();
      this._api = null;
      this._started = false;
    }
  }

  customElements.define('vyago-can', VyagoCan);
})();
